import { PlusIcon } from "lucide-react";

import { Icon } from "@/components/atoms/Icon";
import { useListContext } from "./List.hooks";

import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { cn } from "@/components/ui/utils";

/**
 * A list's one way to make an option it does not have: a row at its foot,
 * always there, that names what it makes - what is typed, where something is,
 * else *label* - and is reached by the arrow keys after the last option, as
 * one of them is, and Enter. The only such row, written in the list's text
 * as its search is (``classes.text``): what is typed and the row that names it are
 * the one size.
 */
export function ListCreateNewFooter({
  label = "Create new…",
}: {
  label?: string;
}) {
  const {
    submitNewRow,
    isCreatingNewRow,
    hasNewButton,
    filter,
    activeIndex,
    navigationData,
    setActiveIndexNoScroll,
    isKeyboardNavigation,
    setIsKeyboardNavigation,
    isMouseEnabled,
  } = useListContext();

  if (!hasNewButton) {
    return null;
  }
  // The keys come to it after the last option.
  const isHighlighted = activeIndex === navigationData.length;

  return (
    <div
      data-testid="list-create-new"
      className="[border-top-width:1px] [border-top-style:solid] border-border p-1 shrink-0 bg-popover"
      onMouseEnter={() => {
        // Not until the pointer moves, after the keys moved the highlight.
        if (!isMouseEnabled) return;
        if (isKeyboardNavigation) {
          setIsKeyboardNavigation(false);
        }
        setActiveIndexNoScroll(navigationData.length);
      }}
    >
      <Button
        variant="ghost"
        size="sm"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => {
          void submitNewRow();
        }}
        disabled={isCreatingNewRow || isCreatingNewRow}
        // In the list's text - its colour the variant's at rest, so under
        // the pointer it is the variant's own - filled while the keys are
        // on it.
        className={cn(
          "text-foreground",
          "[box-shadow:none] justify-start",
          isHighlighted && "bg-accent!",
          "w-full",
        )}
      >
        {isCreatingNewRow ? (
          <Spinner />
        ) : isCreatingNewRow ? null : (
          <Icon ReactIcon={PlusIcon} />
        )}
        {filter ? `Create new ${filter}` : label}
      </Button>
    </div>
  );
}
